// Renders a client message template by substituting {placeholders} with a
// domain's live data. Shared by the Settings preview and the domain-details
// send dialog so both produce the exact same message.
//
// Supported placeholders:
//   {domain}       — the domain name (e.g. syedempire.com)
//   {days}         — absolute number of days to / since expiry (e.g. 169)
//   {expiry_date}  — formatted expiry date (e.g. 02 Jan 2026)
//   {status_line}  — smart phrase: "expired 169 day(s) ago on 02 Jan 2026",
//                    "expires in 12 day(s) on 02 Jan 2026", or
//                    "has no expiry date on record"
//   {client_name}  — the client's name, or "Client" when unknown
//   {company}      — our company name

export const COMPANY_NAME = "RABS Net Solutions Pvt. Ltd.";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "02 Jan 2026" — null/empty safe.
export function formatExpiryDate(date) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// Whole days from today to `date` (negative = already past).
function daysUntil(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

function buildStatusLine(expiry) {
  const days = daysUntil(expiry);
  if (days === null) return "has no expiry date on record";
  const dateStr = formatExpiryDate(expiry);
  if (days < 0) {
    const n = Math.abs(days);
    return `is expired ${n} day(s) ago on ${dateStr}`;
  }
  if (days === 0) return `expires today (${dateStr})`;
  return `expires in ${days} day(s) on ${dateStr}`;
}

// Build the placeholder map for a domain record (as returned by the API).
export function buildPlaceholders(domain = {}) {
  const days = daysUntil(domain.expiry);
  return {
    "{domain}": domain.name || "",
    "{days}": days === null ? "" : String(Math.abs(days)),
    "{expiry_date}": formatExpiryDate(domain.expiry),
    "{status_line}": buildStatusLine(domain.expiry),
    "{client_name}": domain.clientName || "Client",
    "{company}": COMPANY_NAME,
  };
}

// Replace every {placeholder} in `template` using the domain's data.
export function renderTemplate(template, domain) {
  if (!template) return "";
  const map = buildPlaceholders(domain);
  return template.replace(
    /\{(domain|days|expiry_date|status_line|client_name|company)\}/g,
    (match) => (match in map ? map[match] : match)
  );
}

// The placeholder tokens we expose in the Settings help, with a short label.
export const PLACEHOLDER_HELP = [
  { token: "{domain}", label: "Domain name" },
  { token: "{client_name}", label: "Client name" },
  { token: "{expiry_date}", label: "Expiry date" },
  { token: "{days}", label: "Days to/since expiry" },
  { token: "{status_line}", label: "Auto expiry phrase" },
  { token: "{company}", label: "Company name" },
];
